/**
 * The partner's structured location: country (seeded jurisdictions),
 * state / region detail where the country has seeded subjurisdictions
 * (the US today), and the free-text nearest city. A state token belongs
 * to its country, so changing or clearing the country drops it.
 */

import { useMemo } from 'react';

import { Group, Select, TextInput, type TextInputProps } from '@mantine/core';

import { JURISDICTIONS } from '@/constants';
import type { SubjurisdictionInfo } from '@/types/network-partner';

// Searchable by name or code — "Kenya (KE)".
const COUNTRY_OPTIONS = JURISDICTIONS.map((c) => ({
  value: c.alpha2code,
  label: `${c.name} (${c.alpha2code})`,
}));

export interface PartnerLocation {
  country: string | null;
  /** Subjurisdiction token, e.g. `us:new-york`. */
  state: string | null;
}

interface PartnerLocationFieldsProps {
  value: PartnerLocation;
  onChange: (next: PartnerLocation) => void;
  /** The seeded subjurisdictions (all countries) — grouped here. */
  subjurisdictions: SubjurisdictionInfo[];
  /** Bound input props for the nearest-city field (from the form). */
  cityInputProps: TextInputProps;
  readOnly: boolean;
}

/** Whether a state token still belongs to the (new) country. */
function belongsTo(state: string | null, country: string | null): boolean {
  return Boolean(state && country && state.startsWith(`${country.toLowerCase()}:`));
}

export function PartnerLocationFields({
  value,
  onChange,
  subjurisdictions,
  cityInputProps,
  readOnly,
}: Readonly<PartnerLocationFieldsProps>) {
  const stateOptions = useMemo(
    () =>
      subjurisdictions
        .filter((s) => s.jurisdiction_code === value.country)
        .map((s) => ({ value: s.token, label: s.name })),
    [subjurisdictions, value.country]
  );

  return (
    <Group grow align="flex-start">
      <Select
        label="Country"
        description="Where the partner is based"
        placeholder="Search country…"
        data={COUNTRY_OPTIONS}
        searchable
        clearable
        value={value.country}
        onChange={(country) =>
          onChange({ country, state: belongsTo(value.state, country) ? value.state : null })
        }
        disabled={readOnly}
        data-testid="np-location-country"
      />
      {stateOptions.length > 0 && (
        <Select
          label="State / region"
          description="Optional detail"
          placeholder="Pick a state"
          data={stateOptions}
          searchable
          clearable
          value={value.state}
          onChange={(state) => onChange({ country: value.country, state })}
          disabled={readOnly}
          data-testid="np-location-state"
        />
      )}
      <TextInput
        label="Nearest city"
        placeholder="e.g. Nairobi"
        {...cityInputProps}
        data-testid="np-location-city"
      />
    </Group>
  );
}
